import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { loadScript } from '~/lib/browser/loadScript'
import { Button } from '~/components/ui/Button'
import { Container } from '~/components/ui/Container'
import { FormStatus, TextArea, TextField, field } from '~/components/ui/Field'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'

const TITLE = 'Contact Us | Direct Action Everywhere'
const DESCRIPTION =
  'Got questions or suggestions? We would love to hear from you.'

export const Route = createFileRoute('/contact-us')({
  headers: () => cacheHeaders.content,
  head: () => pageMeta({ title: TITLE, description: DESCRIPTION }),
  component: ContactUs,
})

// Same endpoint, payload and reCAPTCHA v2 checkbox as the Webflow-era page script.
const CONTACT_URL =
  'https://vo47l2ia75.execute-api.us-west-2.amazonaws.com/prod/contactform'
const RECAPTCHA_SITE_KEY = '6Lf8UIQUAAAAAItv3Tl2H--vl6qPhcwQD9jds87z'
const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit'

const getGrecaptcha = () => window.grecaptcha

type Status = 'idle' | 'submitting' | 'done'

const ERROR_ID = 'contact-error'
const CAPTCHA_MESSAGE = 'Please complete the captcha.'
const CAPTCHA_LOAD_MESSAGE =
  "The captcha didn't load. Please reload the page and try again."
const SEND_MESSAGE =
  'Oops! Something went wrong while submitting the form. Please try again.'

function ContactUs() {
  const [status, setStatus] = useState<Status>('idle')
  // Why the last attempt didn't go through; the form stays mounted so the
  // visitor's message survives it. `focus` is set by a submit, not by the
  // captcha script failing on page load (which would steal focus).
  const [error, setError] = useState<{
    message: string
    aboutCaptcha: boolean
    focus: boolean
  } | null>(null)
  // Remounts the error message on each submit, so it takes focus again even
  // when the text is the same as last time.
  const [attempt, setAttempt] = useState(0)
  const captchaRef = useRef<HTMLDivElement>(null)
  const widgetId = useRef<number | null>(null)

  useEffect(() => {
    function render() {
      const g = getGrecaptcha()
      if (!g || !captchaRef.current || widgetId.current !== null) return
      g.ready(() => {
        if (!captchaRef.current || widgetId.current !== null) return
        widgetId.current = g.render(captchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
        })
      })
    }
    if (getGrecaptcha()) {
      render()
      return
    }
    loadScript(RECAPTCHA_SRC).then(render, (err) => {
      console.error(err)
      setError({
        message: CAPTCHA_LOAD_MESSAGE,
        aboutCaptcha: true,
        focus: false,
      })
    })
  }, [])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const g = getGrecaptcha()
    setAttempt((n) => n + 1)
    if (!g) {
      setError({
        message: CAPTCHA_LOAD_MESSAGE,
        aboutCaptcha: true,
        focus: true,
      })
      return
    }
    const captcha =
      widgetId.current !== null ? g.getResponse(widgetId.current) : ''
    if (captcha === '') {
      setError({ message: CAPTCHA_MESSAGE, aboutCaptcha: true, focus: true })
      return
    }
    const data = new FormData(e.currentTarget)
    const body = new URLSearchParams({
      // Hidden on the live site, but part of the payload (the endpoint is
      // not ours; keep sending the key rather than risk it rejecting a
      // payload without it).
      name: '',
      email: field(data, 'email'),
      subject: field(data, 'subject'),
      message: field(data, 'message'),
      'g-recaptcha-response': captcha,
    })
    setError(null)
    setStatus('submitting')
    try {
      const res = await fetch(CONTACT_URL, { method: 'POST', body })
      if (!res.ok) throw new Error(`Contact form responded ${res.status}`)
      setStatus('done')
    } catch (err) {
      console.error(err)
      // A reCAPTCHA response is single-use, so the next attempt needs a new one.
      g.reset(widgetId.current ?? undefined)
      setError({ message: SEND_MESSAGE, aboutCaptcha: false, focus: true })
      setStatus('idle')
    }
  }

  return (
    <PageHero as="div" bottom="page">
      <Container width="form">
        <div className="text-center">
          <PageTitle className="mb-5">Contact us</PageTitle>
          <p className="mb-10 text-base leading-6 md:text-lg md:leading-[26px]">
            Got questions or suggestions? We would love to hear from you.
          </p>
        </div>

        {status === 'done' ? (
          // The form is gone, so focus moves to the confirmation.
          <FormStatus focus kind="success">
            Thank you! Your submission has been received!
          </FormStatus>
        ) : (
          <form onSubmit={onSubmit}>
            <TextField
              label="Email Address"
              id="contact-email"
              name="email"
              type="email"
              required
              maxLength={256}
              placeholder="Enter your email"
            />
            <TextField
              label="Subject"
              id="contact-subject"
              name="subject"
              type="text"
              required
              maxLength={256}
              placeholder="Enter a subject"
            />
            <TextArea
              label="Message"
              id="contact-message"
              name="message"
              required
              maxLength={5000}
              placeholder="Message"
              className="h-32 resize-y"
            />
            <div
              ref={captchaRef}
              role="group"
              aria-label="Captcha"
              aria-describedby={error?.aboutCaptcha ? ERROR_ID : undefined}
              className="mb-6 min-h-[78px]"
            />
            {error && (
              <FormStatus
                key={attempt}
                focus={error.focus}
                kind="error"
                id={ERROR_ID}
                className="mb-6"
              >
                {error.message}
              </FormStatus>
            )}
            <div className="text-center">
              <Button
                type="submit"
                aria-describedby={error ? ERROR_ID : undefined}
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Please wait...' : 'Send message'}
              </Button>
            </div>
            <p className="mt-8 mb-0 rounded-sm bg-surface-alt p-8 text-center text-xl leading-7 text-fg">
              For press inquiries, please email{' '}
              <a
                href="mailto:press@dxe.io"
                className="font-semibold text-accent"
              >
                press@dxe.io
              </a>
            </p>
          </form>
        )}
      </Container>
    </PageHero>
  )
}
